/**
 * exportDoc(spec)
 * Exports the rendered API documentation as a standalone HTML file.
 * All styles are inlined. No external dependencies.
 */
export function exportDoc(spec, htmlContent) {
  const styles = `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', monospace; background: #0a0a0a; color: #e8e8e4; font-size: 14px; line-height: 1.6; padding: 0; }
    a { color: #e8ff5a; }
    .export-header { padding: 40px 48px 32px; border-bottom: 1px solid #2a2a2a; }
    .export-title { font-size: 32px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 8px; }
    .export-meta { color: #888; font-size: 13px; }
    .export-meta span { margin-right: 16px; }
    .export-desc { color: #aaa; margin-top: 12px; max-width: 640px; line-height: 1.7; }
    .export-body { padding: 32px 48px; }
    .endpoint { border: 1px solid #2a2a2a; border-radius: 8px; margin-bottom: 12px; overflow: hidden; }
    .endpoint-header { display: flex; align-items: center; gap: 12px; padding: 14px 18px; background: #111; }
    .method-badge { font-family: monospace; font-size: 11px; font-weight: 700; padding: 3px 8px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.06em; }
    .endpoint-path { font-family: monospace; font-size: 13px; color: #e8e8e4; }
    .endpoint-summary { font-size: 12px; color: #888; margin-left: auto; }
    .endpoint-body { padding: 16px 18px; border-top: 1px solid #2a2a2a; background: #0a0a0a; }
    .param-table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
    .param-table th { text-align: left; padding: 6px 10px; border-bottom: 1px solid #2a2a2a; color: #555; font-weight: 500; letter-spacing: 0.06em; font-size: 10px; text-transform: uppercase; }
    .param-table td { padding: 8px 10px; border-bottom: 1px solid #1e1e1e; color: #aaa; vertical-align: top; }
    .param-table td:first-child { font-family: monospace; color: #e8ff5a; }
    .section-label { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: #555; margin-bottom: 8px; margin-top: 14px; }
    pre { background: #111; border: 1px solid #2a2a2a; border-radius: 6px; padding: 12px 14px; overflow-x: auto; font-family: monospace; font-size: 12px; color: #e8e8e4; }
    .required-badge { color: #ff6b6b; font-size: 10px; margin-left: 4px; }
    .tag-section { margin-bottom: 32px; }
    .tag-title { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: #555; border-bottom: 1px solid #2a2a2a; padding-bottom: 10px; margin-bottom: 16px; }
    footer { padding: 24px 48px; border-top: 1px solid #2a2a2a; color: #555; font-size: 11px; letter-spacing: 0.05em; }
  `;

  const methodColors = {
    get: { color: '#e8ff5a', bg: 'rgba(232,255,90,0.12)' },
    post: { color: '#4fffb0', bg: 'rgba(79,255,176,0.12)' },
    put: { color: '#5ab4ff', bg: 'rgba(90,180,255,0.12)' },
    patch: { color: '#ffaa5a', bg: 'rgba(255,170,90,0.12)' },
    delete: { color: '#ff6b6b', bg: 'rgba(255,107,107,0.12)' },
    head: { color: '#c084fc', bg: 'rgba(192,132,252,0.12)' },
    options: { color: '#888', bg: 'rgba(136,136,128,0.12)' },
  };

  const methods = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'];

  let endpointsHTML = '';
  for (const [path, pathItem] of Object.entries(spec.paths || {})) {
    for (const method of methods) {
      const op = pathItem[method];
      if (!op) continue;
      const mc = methodColors[method] || methodColors.options;
      const params = op.parameters || pathItem.parameters || [];

      let paramsHTML = '';
      if (params.length > 0) {
        paramsHTML = `
          <div class="section-label">Parameters</div>
          <table class="param-table">
            <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
            <tbody>
              ${params.map(p => `
                <tr>
                  <td>${p.name || ''}</td>
                  <td>${p.in || ''}</td>
                  <td>${p.schema?.type || p.type || '—'}</td>
                  <td>${p.required ? '<span style="color:#ff6b6b">yes</span>' : '<span style="color:#555">no</span>'}</td>
                  <td>${p.description || '—'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      }

      let responsesHTML = '';
      if (op.responses) {
        responsesHTML = `<div class="section-label" style="margin-top:14px">Responses</div>`;
        for (const [code, resp] of Object.entries(op.responses)) {
          responsesHTML += `<div style="display:flex;gap:10px;align-items:baseline;padding:6px 0;border-bottom:1px solid #1e1e1e;">
            <span style="font-family:monospace;color:${code.startsWith('2') ? '#4fffb0' : code.startsWith('4') ? '#ffaa5a' : '#ff6b6b'};font-size:12px">${code}</span>
            <span style="color:#666;font-size:12px">${resp.description || ''}</span>
          </div>`;
        }
      }

      endpointsHTML += `
        <div class="endpoint">
          <div class="endpoint-header">
            <span class="method-badge" style="color:${mc.color};background:${mc.bg}">${method.toUpperCase()}</span>
            <span class="endpoint-path">${path}</span>
            <span class="endpoint-summary">${op.summary || ''}</span>
          </div>
          ${paramsHTML || responsesHTML ? `
          <div class="endpoint-body">
            ${op.description ? `<p style="color:#888;font-size:13px;margin-bottom:12px">${op.description}</p>` : ''}
            ${paramsHTML}
            ${responsesHTML}
          </div>` : ''}
        </div>
      `;
    }
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${spec.info.title} — API Documentation</title>
  <style>${styles}</style>
</head>
<body>
  <div class="export-header">
    <div class="export-title">${spec.info.title}</div>
    <div class="export-meta">
      <span>v${spec.info.version}</span>
      ${spec.servers[0]?.url ? `<span>${spec.servers[0].url}</span>` : ''}
      <span>Generated by APIScribe</span>
    </div>
    ${spec.info.description ? `<div class="export-desc">${spec.info.description}</div>` : ''}
  </div>
  <div class="export-body">
    ${endpointsHTML}
  </div>
  <footer>Generated with APIScribe · ${new Date().toISOString().split('T')[0]}</footer>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${spec.info.title.replace(/\s+/g, '-').toLowerCase()}-api-docs.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
